import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from 'aws-amplify/auth';
import './AutomationManager.css';
import '../Lead/styles/LeadTracker.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://track.snapped.cc';

const AutomationManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [automations, setAutomations] = useState([]);
  const [filteredAutomations, setFilteredAutomations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // View mode
  const [viewMode, setViewMode] = useState('grouped'); // 'table' or 'grouped'
  
  // Expanded rows for logs - using object instead of Set for proper React state updates
  const [expandedRows, setExpandedRows] = useState({});
  const [executionLogs, setExecutionLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  
  // Expanded categories for accordion
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [editForm, setEditForm] = useState({
    schedule_type: 'interval',
    interval_seconds: 300,
    crontab_hour: '0',
    crontab_minute: '0',
    crontab_day_of_week: '*',
    process_group: '',
    process_order: 0,
    category: '',
    display_name: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Favorites
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  
  // Quick link dropdown
  const [linkDropdownOpen, setLinkDropdownOpen] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  
  // Show/hide unused section
  const [showUnusedSection, setShowUnusedSection] = useState(true);
  const [unusedCollapsed, setUnusedCollapsed] = useState(true);

  useEffect(() => {
    fetchAutomations();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterAutomations();
  }, [automations, searchTerm, selectedCategory]);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/automations`);
      setAutomations(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching automations:', err);
      setError('Failed to load automations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/automations/categories`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const filterAutomations = () => {
    let filtered = automations;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(auto => auto.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(auto =>
        auto.name.toLowerCase().includes(term) ||
        auto.description.toLowerCase().includes(term) ||
        auto.automation_id.toLowerCase().includes(term)
      );
    }

    setFilteredAutomations(filtered);
  };

  const toggleAutomation = async (automationId, currentlyEnabled) => {
    try {
      await axios.put(`${API_BASE_URL}/api/automations/${automationId}/toggle`, {
        enabled: !currentlyEnabled
      });
      
      // Update local state
      setAutomations(prevAutomations =>
        prevAutomations.map(auto =>
          auto.automation_id === automationId
            ? { ...auto, enabled: !currentlyEnabled }
            : auto
        )
      );
    } catch (err) {
      console.error('Error toggling automation:', err);
    }
  };

  const toggleUnused = async (automationId, currentlyUnused) => {
    // Show confirmation when moving TO unused (not when restoring)
    if (!currentlyUnused) {
      const automation = automations.find(a => a.automation_id === automationId);
      const confirmed = window.confirm(
        `Move "${automation?.name || automationId}" to Unused?\n\nThis will hide it from the main grid.`
      );
      if (!confirmed) return;
    }
    
    try {
      await axios.put(`${API_BASE_URL}/api/automations/${automationId}`, {
        unused: !currentlyUnused
      });
      
      // Update local state
      setAutomations(prevAutomations =>
        prevAutomations.map(auto =>
          auto.automation_id === automationId
            ? { ...auto, unused: !currentlyUnused }
            : auto
        )
      );
    } catch (err) {
      console.error('Error toggling unused:', err);
      alert('Failed to toggle automation. Please try again.');
    }
  };

  const toggleRowExpansion = async (automationId) => {
    const isExpanded = expandedRows[automationId];
    
    if (isExpanded) {
      setExpandedRows(prev => {
        const newState = { ...prev };
        delete newState[automationId];
        return newState;
      });
    } else {
      setExpandedRows(prev => ({ ...prev, [automationId]: true }));
      // Fetch logs if not already loaded
      if (!executionLogs[automationId]) {
        await fetchExecutionLogs(automationId);
      }
    }
  };

  const fetchExecutionLogs = async (automationId) => {
    try {
      setLoadingLogs(prev => ({ ...prev, [automationId]: true }));
      const response = await axios.get(`${API_BASE_URL}/api/automations/${automationId}/logs?limit=10`);
      setExecutionLogs(prev => ({ ...prev, [automationId]: response.data.logs }));
    } catch (err) {
      console.error('Error fetching execution logs:', err);
    } finally {
      setLoadingLogs(prev => ({ ...prev, [automationId]: false }));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'success':
        return 'automgmt-status-badge automgmt-status-success';
      case 'failure':
        return 'automgmt-status-badge automgmt-status-failure';
      case 'running':
        return 'automgmt-status-badge automgmt-status-running';
      default:
        return 'automgmt-status-badge automgmt-status-unknown';
    }
  };

  const getCategoryBadgeClass = (category) => {
    const categoryMap = {
      'content_management': 'automgmt-category-badge automgmt-category-content',
      'email_notifications': 'automgmt-category-badge automgmt-category-email',
      'payments': 'automgmt-category-badge automgmt-category-payments',
      'analytics': 'automgmt-category-badge automgmt-category-analytics',
      'system_monitoring': 'automgmt-category-badge automgmt-category-system',
      'client_onboarding': 'automgmt-category-badge automgmt-category-onboarding',
      'task_management': 'automgmt-category-badge automgmt-category-tasks',
      'messaging': 'automgmt-category-badge automgmt-category-messaging',
      'contracts': 'automgmt-category-badge automgmt-category-contracts',
      'social_media': 'automgmt-category-badge automgmt-category-social'
    };
    return categoryMap[category] || 'automgmt-category-badge automgmt-category-other';
  };

  const formatCategoryName = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getSuccessRate = (automation) => {
    if (automation.execution_count === 0) return 'N/A';
    const rate = ((automation.success_count / automation.execution_count) * 100).toFixed(1);
    return `${rate}%`;
  };

  const getSuccessRateClass = (automation) => {
    if (automation.execution_count === 0) return 'automgmt-success-rate-na';
    const rate = (automation.success_count / automation.execution_count) * 100;
    if (rate >= 95) return 'automgmt-success-rate-good';
    if (rate >= 80) return 'automgmt-success-rate-ok';
    return 'automgmt-success-rate-bad';
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const formatDuration = (duration) => {
    if (!duration) return '-';
    return duration;
  };

  const getGroupedAutomations = () => {
    const grouped = {};
    
    filteredAutomations.forEach(automation => {
      // Skip unused automations - they go to the Unused section
      if (automation.unused) return;
      
      const category = automation.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(automation);
    });
    
    return grouped;
  };

  const getUnusedAutomations = () => {
    const grouped = {};
    
    filteredAutomations.filter(a => a.unused).forEach(automation => {
      const category = automation.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(automation);
    });
    
    return grouped;
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAllCategories = () => {
    const allCategories = Object.keys(getGroupedAutomations());
    const newExpandedState = {};
    allCategories.forEach(cat => {
      newExpandedState[cat] = true;
    });
    setExpandedCategories(newExpandedState);
  };

  const collapseAllCategories = () => {
    setExpandedCategories({});
  };

  const getCategoryStats = (categoryAutomations) => {
    const enabled = categoryAutomations.filter(a => a.enabled).length;
    const disabled = categoryAutomations.length - enabled;
    return { total: categoryAutomations.length, enabled, disabled };
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Force navigation to login after sign out
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const openEditModal = async (automation, e) => {
    e.stopPropagation();
    setSelectedAutomation(automation);
    
    // Fetch full automation details to get current schedule settings
    try {
      const response = await axios.get(`${API_BASE_URL}/api/automations/${automation.automation_id}`);
      const details = response.data;
      
      setEditForm({
        schedule_type: details.schedule_type || 'interval',
        interval_seconds: details.interval_seconds || 300,
        crontab_hour: details.crontab?.hour || '0',
        crontab_minute: details.crontab?.minute || '0',
        crontab_day_of_week: details.crontab?.day_of_week || '*',
        process_group: details.process_group || '',
        process_order: details.process_order || 0,
        category: details.category || '',
        display_name: details.name || '',
        description: details.description || ''
      });
    } catch (err) {
      console.error('Error fetching automation details:', err);
      // Use defaults if fetch fails
      setEditForm({
        schedule_type: automation.schedule_type || 'interval',
        interval_seconds: 300,
        crontab_hour: '0',
        crontab_minute: '0',
        crontab_day_of_week: '*',
        process_group: automation.process_group || '',
        process_order: automation.process_order || 0,
        category: automation.category || '',
        display_name: automation.name || '',
        description: automation.description || ''
      });
    }
    
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedAutomation(null);
  };

  const saveSchedule = async () => {
    if (!selectedAutomation) return;
    
    setSaving(true);
    try {
      const updateData = {
        schedule_type: editForm.schedule_type,
        process_group: editForm.process_group,
        process_order: parseInt(editForm.process_order, 10) || 0,
        category: editForm.category,
        display_name: editForm.display_name,
        description: editForm.description
      };
      
      if (editForm.schedule_type === 'interval') {
        updateData.interval_seconds = parseInt(editForm.interval_seconds, 10);
      } else {
        updateData.crontab = {
          minute: editForm.crontab_minute,
          hour: editForm.crontab_hour,
          day_of_week: editForm.crontab_day_of_week,
          day_of_month: '*',
          month_of_year: '*'
        };
      }
      
      await axios.put(`${API_BASE_URL}/api/automations/${selectedAutomation.automation_id}`, updateData);
      
      // Refresh automations to show updated schedule
      await fetchAutomations();
      
      closeEditModal();
      alert('Schedule updated successfully!');
    } catch (err) {
      console.error('Error saving schedule:', err);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getIntervalOptions = () => [
    { value: 60, label: 'Every 1 minute' },
    { value: 120, label: 'Every 2 minutes' },
    { value: 300, label: 'Every 5 minutes' },
    { value: 600, label: 'Every 10 minutes' },
    { value: 900, label: 'Every 15 minutes' },
    { value: 1800, label: 'Every 30 minutes' },
    { value: 3600, label: 'Every 1 hour' },
    { value: 7200, label: 'Every 2 hours' },
    { value: 14400, label: 'Every 4 hours' },
    { value: 21600, label: 'Every 6 hours' },
    { value: 43200, label: 'Every 12 hours' },
    { value: 86400, label: 'Every 24 hours' },
  ];

  const toggleFavorite = async (automationId, currentlyFavorited, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_BASE_URL}/api/automations/${automationId}`, {
        favorited: !currentlyFavorited
      });
      
      // Update local state
      setAutomations(prevAutomations =>
        prevAutomations.map(auto =>
          auto.automation_id === automationId
            ? { ...auto, favorited: !currentlyFavorited }
            : auto
        )
      );
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert('Failed to update favorite. Please try again.');
    }
  };

  const getFavorites = () => {
    return automations.filter(auto => auto.favorited);
  };

  const getProcessGroups = () => {
    const groups = new Set();
    automations.forEach(auto => {
      if (auto.process_group) {
        groups.add(auto.process_group);
      }
    });
    return Array.from(groups).sort();
  };

  const sortByProcessGroup = (automationsList) => {
    return [...automationsList].sort((a, b) => {
      // First sort by process_group (empty groups at end)
      if (a.process_group && !b.process_group) return -1;
      if (!a.process_group && b.process_group) return 1;
      if (a.process_group !== b.process_group) {
        return (a.process_group || '').localeCompare(b.process_group || '');
      }
      // Then by process_order within same group
      return (a.process_order || 0) - (b.process_order || 0);
    });
  };

  const quickLinkToGroup = async (automationId, groupName, e) => {
    e.stopPropagation();
    try {
      // Calculate next order for this group
      const groupAutomations = automations.filter(a => a.process_group === groupName);
      const nextOrder = groupAutomations.length + 1;
      
      await axios.put(`${API_BASE_URL}/api/automations/${automationId}`, {
        process_group: groupName,
        process_order: nextOrder
      });
      
      // Update local state
      setAutomations(prevAutomations =>
        prevAutomations.map(auto =>
          auto.automation_id === automationId
            ? { ...auto, process_group: groupName, process_order: nextOrder }
            : auto
        )
      );
      setLinkDropdownOpen(null);
      setNewGroupName('');
    } catch (err) {
      console.error('Error linking automation:', err);
      alert('Failed to link. Please try again.');
    }
  };

  const unlinkFromGroup = async (automationId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_BASE_URL}/api/automations/${automationId}`, {
        process_group: '',
        process_order: 0
      });
      
      setAutomations(prevAutomations =>
        prevAutomations.map(auto =>
          auto.automation_id === automationId
            ? { ...auto, process_group: '', process_order: 0 }
            : auto
        )
      );
      setLinkDropdownOpen(null);
    } catch (err) {
      console.error('Error unlinking automation:', err);
    }
  };

  const toggleLinkDropdown = (automationId, e) => {
    e.stopPropagation();
    setLinkDropdownOpen(linkDropdownOpen === automationId ? null : automationId);
    setNewGroupName('');
  };

  if (loading) {
    return <div className="automgmt-automation-manager-loading">Loading automations...</div>;
  }

  if (error) {
    return <div className="automgmt-automation-manager-error">{error}</div>;
  }

  const groupedAutomations = getGroupedAutomations();

  return (
    <div className="automgmt-automation-manager">
      <div className="automgmt-automation-header">
        <h1>Automation Manager</h1>
        <p className="automgmt-automation-subtitle">
          Manage and monitor all system automations
        </p>
      </div>

      <div className="automgmt-automation-filters">
        <div className="automgmt-filter-group">
          <input
            type="text"
            className="automgmt-search-input"
            placeholder="Search automations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="automgmt-filter-group">
          <select
            className="automgmt-category-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {formatCategoryName(category)}
              </option>
            ))}
          </select>
        </div>

        <div className="automgmt-view-mode-toggle">
          <button 
            className={`automgmt-view-mode-button ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Table View
          </button>
          <button 
            className={`automgmt-view-mode-button ${viewMode === 'grouped' ? 'active' : ''}`}
            onClick={() => setViewMode('grouped')}
          >
            Grouped View
          </button>
        </div>

        <button 
          className={`automgmt-visibility-toggle ${!showUnusedSection ? 'hidden-mode' : ''}`}
          onClick={() => setShowUnusedSection(!showUnusedSection)}
          title={showUnusedSection ? 'Hide Unused section' : 'Show Unused section'}
        >
          <span className="automgmt-eye-icon">{showUnusedSection ? '◉' : '○'}</span>
          Unused ({filteredAutomations.filter(a => a.unused).length})
        </button>

        <div className="automgmt-automation-stats-summary">
          <span className="automgmt-stat-item">
            Total: <strong>{automations.length}</strong>
          </span>
          <span className="automgmt-stat-item">
            Enabled: <strong>{automations.filter(a => a.enabled).length}</strong>
          </span>
          <span className="automgmt-stat-item">
            Disabled: <strong>{automations.filter(a => !a.enabled).length}</strong>
          </span>
        </div>
      </div>

      {/* Favorites Dropdown */}
      {getFavorites().length > 0 && (
        <div className="automgmt-favorites-section">
          <div 
            className="automgmt-favorites-header"
            onClick={() => setFavoritesOpen(!favoritesOpen)}
          >
            <span className="automgmt-favorites-star">★</span>
            <span className="automgmt-favorites-title">Favorites</span>
            <span className="automgmt-favorites-count">{getFavorites().length}</span>
            <span className={`automgmt-favorites-chevron ${favoritesOpen ? 'open' : ''}`}>▼</span>
          </div>
          {favoritesOpen && (
            <div className="automgmt-favorites-dropdown">
              {getFavorites().map(automation => (
                <div 
                  key={automation.automation_id} 
                  className="automgmt-favorites-item"
                  onClick={() => toggleRowExpansion(automation.automation_id)}
                >
                  <div className="automgmt-favorites-item-info">
                    <div className="automgmt-favorites-item-name">{automation.name}</div>
                    <div className="automgmt-favorites-item-schedule">{automation.schedule}</div>
                  </div>
                  <div className="automgmt-favorites-item-actions">
                    <span className={`automgmt-favorites-item-status ${automation.enabled ? 'enabled' : 'disabled'}`}>
                      {automation.enabled ? 'On' : 'Off'}
                    </span>
                    <button 
                      className="automgmt-favorites-item-edit"
                      onClick={(e) => openEditModal(automation, e)}
                      title="Edit Schedule"
                    >
                      Edit
                    </button>
                    <button 
                      className="automgmt-favorites-item-unfav"
                      onClick={(e) => toggleFavorite(automation.automation_id, true, e)}
                      title="Remove from favorites"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewMode === 'grouped' && (
        <div className="automgmt-expand-controls">
          <button className="automgmt-expand-btn" onClick={expandAllCategories}>Expand All</button>
          <button className="automgmt-expand-btn" onClick={collapseAllCategories}>Collapse All</button>
        </div>
      )}

      {filteredAutomations.length === 0 ? (
        <div className="automgmt-no-results">
          No automations found matching your filters.
        </div>
      ) : viewMode === 'table' ? (
        <div className="automgmt-automation-table-container">
          <table className="automgmt-automation-table">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Category</th>
                <th>Schedule</th>
                <th>Last Run</th>
                <th>Status</th>
                <th>Success Rate</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAutomations.map(automation => (
                <React.Fragment key={automation.automation_id}>
                  <tr 
                    className={`automgmt-automation-row ${expandedRows[automation.automation_id] ? 'automgmt-expanded' : ''}`}
                    onClick={() => toggleRowExpansion(automation.automation_id)}
                  >
                    <td className="automgmt-expand-cell">
                      <span className={`automgmt-expand-icon ${expandedRows[automation.automation_id] ? 'automgmt-expanded' : ''}`}>
                        ▶
                      </span>
                    </td>
                    <td className="automgmt-name-cell">
                      <div className="automgmt-automation-name">{automation.name}</div>
                      <div className={`automgmt-automation-description ${!automation.description ? 'empty' : ''}`}>
                        {automation.description || 'No description available'}
                      </div>
                    </td>
                    <td>
                      <span className={getCategoryBadgeClass(automation.category)}>
                        {formatCategoryName(automation.category)}
                      </span>
                    </td>
                    <td className="automgmt-schedule-cell">
                      {automation.schedule}
                    </td>
                    <td className="automgmt-datetime-cell">{formatDateTime(automation.last_run)}</td>
                    <td>
                      {automation.last_status && (
                        <span className={getStatusBadgeClass(automation.last_status)}>
                          {automation.last_status}
                        </span>
                      )}
                      {!automation.last_status && '-'}
                    </td>
                    <td>
                      <span className={`automgmt-success-rate ${getSuccessRateClass(automation)}`}>
                        {getSuccessRate(automation)}
                      </span>
                      <div className="automgmt-execution-count">
                        {automation.execution_count} runs
                      </div>
                    </td>
                    <td className="automgmt-toggle-cell" onClick={(e) => e.stopPropagation()}>
                      <label className="automgmt-toggle-switch">
                        <input
                          type="checkbox"
                          checked={automation.enabled}
                          onChange={() => toggleAutomation(automation.automation_id, automation.enabled)}
                        />
                        <span className="automgmt-toggle-slider"></span>
                      </label>
                    </td>
                    <td className="automgmt-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="automgmt-actions-row">
                        <button 
                          className={`automgmt-fav-btn ${automation.favorited ? 'favorited' : ''}`}
                          onClick={(e) => toggleFavorite(automation.automation_id, automation.favorited, e)}
                          title={automation.favorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {automation.favorited ? '★' : '☆'}
                        </button>
                        <button 
                          className="automgmt-action-btn automgmt-trash-btn"
                          onClick={(e) => { e.stopPropagation(); toggleUnused(automation.automation_id, automation.unused); }}
                          title="Move to Unused"
                        >
                          🗑
                        </button>
                        <div className="automgmt-link-wrapper">
                          <button 
                            className={`automgmt-action-btn automgmt-link-btn ${automation.process_group ? 'linked' : ''}`}
                            onClick={(e) => toggleLinkDropdown(automation.automation_id, e)}
                            title={automation.process_group ? `Linked: ${automation.process_group}` : 'Link to group'}
                          >
                            ⛓
                          </button>
                          {linkDropdownOpen === automation.automation_id && (
                            <div className="automgmt-link-dropdown">
                              <div className="automgmt-link-dropdown-header">Link to Process Group</div>
                              {automation.process_group && (
                                <button 
                                  className="automgmt-link-option automgmt-unlink"
                                  onClick={(e) => unlinkFromGroup(automation.automation_id, e)}
                                >
                                  Unlink from "{automation.process_group}"
                                </button>
                              )}
                              {getProcessGroups().length > 0 && (
                                <div className="automgmt-link-existing">
                                  <div className="automgmt-link-section-label">Existing groups:</div>
                                  {getProcessGroups().map(group => (
                                    <button 
                                      key={group}
                                      className="automgmt-link-option"
                                      onClick={(e) => quickLinkToGroup(automation.automation_id, group, e)}
                                    >
                                      {group}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div className="automgmt-link-new">
                                <input
                                  type="text"
                                  placeholder="New group name..."
                                  value={newGroupName}
                                  onChange={(e) => setNewGroupName(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="automgmt-link-input"
                                />
                                {newGroupName && (
                                  <button 
                                    className="automgmt-link-create"
                                    onClick={(e) => quickLinkToGroup(automation.automation_id, newGroupName, e)}
                                  >
                                    Create & Link
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <button 
                          className="automgmt-action-btn automgmt-edit-btn"
                          onClick={(e) => openEditModal(automation, e)}
                          title="Edit"
                        >
                          ✎
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {expandedRows[automation.automation_id] && (
                    <tr className="automgmt-expanded-logs-row">
                      <td colSpan="9">
                        <div className="automgmt-expanded-details">
                          <div className="automgmt-details-section">
                            <h4>About This Automation</h4>
                            <div className="automgmt-details-grid">
                              <div className="automgmt-detail-item">
                                <span className="automgmt-detail-label">Description</span>
                                <span className="automgmt-detail-value">{automation.description || 'No description available'}</span>
                              </div>
                              <div className="automgmt-detail-item">
                                <span className="automgmt-detail-label">Task</span>
                                <span className="automgmt-detail-value automgmt-detail-code">{automation.task || 'N/A'}</span>
                              </div>
                              <div className="automgmt-detail-item">
                                <span className="automgmt-detail-label">Schedule</span>
                                <span className="automgmt-detail-value">{automation.schedule}</span>
                              </div>
                              <div className="automgmt-detail-item">
                                <span className="automgmt-detail-label">Category</span>
                                <span className="automgmt-detail-value">{formatCategoryName(automation.category)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="automgmt-execution-logs-container">
                          <h4>Recent Executions</h4>
                          {loadingLogs[automation.automation_id] ? (
                            <div className="automgmt-logs-loading">Loading logs...</div>
                          ) : executionLogs[automation.automation_id]?.length > 0 ? (
                            <table className="automgmt-logs-table">
                              <thead>
                                <tr>
                                  <th>Time</th>
                                  <th>Status</th>
                                  <th>Duration</th>
                                  <th>Result</th>
                                </tr>
                              </thead>
                              <tbody>
                                {executionLogs[automation.automation_id].map(log => (
                                  <tr key={log.execution_id} className={`automgmt-log-row automgmt-log-${log.status}`}>
                                    <td className="automgmt-log-time">{formatDateTime(log.started_at)}</td>
                                    <td>
                                      <span className={getStatusBadgeClass(log.status)}>
                                        {log.status}
                                      </span>
                                    </td>
                                    <td>{formatDuration(log.execution_time)}</td>
                                    <td className="automgmt-log-result">
                                      {log.result_summary || log.error_message || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="automgmt-no-logs">No execution history available</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="automgmt-grouped-view">
          <div className="automgmt-accordion">
            {Object.entries(getGroupedAutomations())
              .sort((a, b) => formatCategoryName(a[0]).localeCompare(formatCategoryName(b[0])))
              .map(([category, categoryAutomations]) => {
                const stats = getCategoryStats(categoryAutomations);
                return (
                  <div key={category} className="automgmt-accordion-item">
                    <div 
                      className="automgmt-accordion-header"
                      onClick={() => toggleCategory(category)}
                    >
                      <div className="automgmt-accordion-header-left">
                        <span className={`automgmt-accordion-chevron ${expandedCategories[category] ? 'expanded' : ''}`}>
                          ▶
                        </span>
                        <span className={getCategoryBadgeClass(category)}>
                          {formatCategoryName(category)}
                        </span>
                        <span className="automgmt-accordion-count">{stats.total}</span>
                      </div>
                      <div className="automgmt-accordion-stats">
                        <span className="automgmt-accordion-stat-enabled">
                          {stats.enabled} enabled
                        </span>
                        <span className="automgmt-accordion-stat-disabled">
                          {stats.disabled} disabled
                        </span>
                      </div>
                    </div>
                    
                    {expandedCategories[category] && (
                      <div className="automgmt-accordion-content">
                        <table className="automgmt-automation-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Name</th>
                              <th>Schedule</th>
                              <th>Last Run</th>
                              <th>Status</th>
                              <th>Success Rate</th>
                              <th>Enabled</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortByProcessGroup(categoryAutomations).map((automation, idx, arr) => {
                              const prevAuto = idx > 0 ? arr[idx - 1] : null;
                              const nextAuto = idx < arr.length - 1 ? arr[idx + 1] : null;
                              const isGroupStart = automation.process_group && (!prevAuto || prevAuto.process_group !== automation.process_group);
                              const isGroupMiddle = automation.process_group && prevAuto?.process_group === automation.process_group && nextAuto?.process_group === automation.process_group;
                              const isGroupEnd = automation.process_group && (!nextAuto || nextAuto.process_group !== automation.process_group) && prevAuto?.process_group === automation.process_group;
                              const isInGroup = automation.process_group && (isGroupStart || isGroupMiddle || isGroupEnd || (prevAuto?.process_group === automation.process_group));
                              
                              return (
                              <React.Fragment key={automation.automation_id}>
                                {isGroupStart && (
                                  <tr className="automgmt-group-header-row">
                                    <td colSpan="8">
                                      <div className="automgmt-group-label">{automation.process_group}</div>
                                    </td>
                                  </tr>
                                )}
                                <tr 
                                  className={`automgmt-automation-row ${expandedRows[automation.automation_id] ? 'automgmt-expanded' : ''} ${isInGroup ? 'automgmt-in-group' : ''} ${isGroupEnd ? 'automgmt-group-end' : ''}`}
                                  onClick={() => toggleRowExpansion(automation.automation_id)}
                                >
                                  <td className="automgmt-expand-cell">
                                    <span className={`automgmt-expand-icon ${expandedRows[automation.automation_id] ? 'automgmt-expanded' : ''}`}>
                                      ▶
                                    </span>
                                  </td>
                                  <td className="automgmt-name-cell">
                                    <div className="automgmt-name-with-step">
                                      {isInGroup && (
                                        <span className="automgmt-step-badge">Step {automation.process_order || 1}</span>
                                      )}
                                      <div className="automgmt-automation-name">{automation.name}</div>
                                    </div>
                                    <div className={`automgmt-automation-description ${!automation.description ? 'empty' : ''}`}>
                                      {automation.description || 'No description available'}
                                    </div>
                                  </td>
                                  <td className="automgmt-schedule-cell">{automation.schedule}</td>
                                  <td className="automgmt-datetime-cell">{formatDateTime(automation.last_run)}</td>
                                  <td>
                                    {automation.last_status && (
                                      <span className={getStatusBadgeClass(automation.last_status)}>
                                        {automation.last_status}
                                      </span>
                                    )}
                                    {!automation.last_status && '-'}
                                  </td>
                                  <td>
                                    <span className={`automgmt-success-rate ${getSuccessRateClass(automation)}`}>
                                      {getSuccessRate(automation)}
                                    </span>
                                    <div className="automgmt-execution-count">
                                      {automation.execution_count} runs
                                    </div>
                                  </td>
                                  <td className="automgmt-toggle-cell" onClick={(e) => e.stopPropagation()}>
                                    <label className="automgmt-toggle-switch">
                                      <input
                                        type="checkbox"
                                        checked={automation.enabled}
                                        onChange={() => toggleAutomation(automation.automation_id, automation.enabled)}
                                      />
                                      <span className="automgmt-toggle-slider"></span>
                                    </label>
                                  </td>
                                  <td className="automgmt-actions-cell" onClick={(e) => e.stopPropagation()}>
                                    <div className="automgmt-actions-row">
                                      <button 
                                        className={`automgmt-fav-btn ${automation.favorited ? 'favorited' : ''}`}
                                        onClick={(e) => toggleFavorite(automation.automation_id, automation.favorited, e)}
                                        title={automation.favorited ? 'Remove from favorites' : 'Add to favorites'}
                                      >
                                        {automation.favorited ? '★' : '☆'}
                                      </button>
                                      <button 
                                        className="automgmt-action-btn automgmt-unused-btn"
                                        onClick={(e) => { e.stopPropagation(); toggleUnused(automation.automation_id, automation.unused); }}
                                        title="Move to Unused"
                                      >
                                        ○
                                      </button>
                                      <div className="automgmt-link-wrapper">
                                        <button 
                                          className={`automgmt-action-btn automgmt-link-btn ${automation.process_group ? 'linked' : ''}`}
                                          onClick={(e) => toggleLinkDropdown(automation.automation_id, e)}
                                          title={automation.process_group ? `Linked: ${automation.process_group}` : 'Link to group'}
                                        >
                                          ⛓
                                        </button>
                                        {linkDropdownOpen === automation.automation_id && (
                                          <div className="automgmt-link-dropdown">
                                            <div className="automgmt-link-dropdown-header">Link to Process Group</div>
                                            {automation.process_group && (
                                              <button 
                                                className="automgmt-link-option automgmt-unlink"
                                                onClick={(e) => unlinkFromGroup(automation.automation_id, e)}
                                              >
                                                Unlink from "{automation.process_group}"
                                              </button>
                                            )}
                                            {getProcessGroups().length > 0 && (
                                              <div className="automgmt-link-existing">
                                                <div className="automgmt-link-section-label">Existing groups:</div>
                                                {getProcessGroups().map(group => (
                                                  <button 
                                                    key={group}
                                                    className="automgmt-link-option"
                                                    onClick={(e) => quickLinkToGroup(automation.automation_id, group, e)}
                                                  >
                                                    {group}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                            <div className="automgmt-link-new">
                                              <input
                                                type="text"
                                                placeholder="New group name..."
                                                value={newGroupName}
                                                onChange={(e) => setNewGroupName(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="automgmt-link-input"
                                              />
                                              {newGroupName && (
                                                <button 
                                                  className="automgmt-link-create"
                                                  onClick={(e) => quickLinkToGroup(automation.automation_id, newGroupName, e)}
                                                >
                                                  Create & Link
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <button 
                                        className="automgmt-action-btn automgmt-edit-btn"
                                        onClick={(e) => openEditModal(automation, e)}
                                        title="Edit"
                                      >
                                        ✎
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                
                                {expandedRows[automation.automation_id] && (
                                  <tr className="automgmt-expanded-logs-row">
                                    <td colSpan="8">
                                      <div className="automgmt-expanded-details">
                                        <div className="automgmt-details-section">
                                          <h4>About This Automation</h4>
                                          <div className="automgmt-details-grid">
                                            <div className="automgmt-detail-item">
                                              <span className="automgmt-detail-label">Description</span>
                                              <span className="automgmt-detail-value">{automation.description || 'No description available'}</span>
                                            </div>
                                            <div className="automgmt-detail-item">
                                              <span className="automgmt-detail-label">Task</span>
                                              <span className="automgmt-detail-value automgmt-detail-code">{automation.task || 'N/A'}</span>
                                            </div>
                                            <div className="automgmt-detail-item">
                                              <span className="automgmt-detail-label">Schedule</span>
                                              <span className="automgmt-detail-value">{automation.schedule}</span>
                                            </div>
                                            <div className="automgmt-detail-item">
                                              <span className="automgmt-detail-label">Category</span>
                                              <span className="automgmt-detail-value">{formatCategoryName(automation.category)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="automgmt-execution-logs-container">
                                        <h4>Recent Executions</h4>
                                        {loadingLogs[automation.automation_id] ? (
                                          <div className="automgmt-logs-loading">Loading logs...</div>
                                        ) : executionLogs[automation.automation_id]?.length > 0 ? (
                                          <table className="automgmt-logs-table">
                                            <thead>
                                              <tr>
                                                <th>Time</th>
                                                <th>Status</th>
                                                <th>Duration</th>
                                                <th>Result</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {executionLogs[automation.automation_id].map(log => (
                                                <tr key={log.execution_id} className={`automgmt-log-row automgmt-log-${log.status}`}>
                                                  <td className="automgmt-log-time">{formatDateTime(log.started_at)}</td>
                                                  <td>
                                                    <span className={getStatusBadgeClass(log.status)}>
                                                      {log.status}
                                                    </span>
                                                  </td>
                                                  <td>{formatDuration(log.execution_time)}</td>
                                                  <td className="automgmt-log-result">
                                                    {log.result_summary || log.error_message || '-'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        ) : (
                                          <div className="automgmt-no-logs">No execution history available</div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Unused Section - shows automations marked as unused */}
            {showUnusedSection && Object.keys(getUnusedAutomations()).length > 0 && (
              <div className={`automgmt-unused-section ${unusedCollapsed ? 'collapsed' : ''}`}>
                <div 
                  className="automgmt-unused-header"
                  onClick={() => setUnusedCollapsed(!unusedCollapsed)}
                >
                  <span className="automgmt-unused-icon">🗑</span>
                  <span className="automgmt-unused-title">Unused</span>
                  <span className="automgmt-unused-count">
                    {Object.values(getUnusedAutomations()).flat().length}
                  </span>
                  <span className={`automgmt-unused-chevron ${unusedCollapsed ? '' : 'expanded'}`}>›</span>
                </div>
                {!unusedCollapsed && (
                  <div className="automgmt-unused-content">
                    {Object.entries(getUnusedAutomations())
                      .sort((a, b) => formatCategoryName(a[0]).localeCompare(formatCategoryName(b[0])))
                      .map(([category, categoryAutomations]) => (
                        <div key={category} className="automgmt-unused-category">
                          <div className="automgmt-unused-category-label">
                            {formatCategoryName(category)} ({categoryAutomations.length})
                          </div>
                          {categoryAutomations.map(automation => (
                            <div key={automation.automation_id} className="automgmt-unused-item">
                              <div className="automgmt-unused-item-info">
                                <div className="automgmt-unused-item-name">{automation.name}</div>
                                <div className="automgmt-unused-item-desc">{automation.description || 'No description'}</div>
                              </div>
                              <div className="automgmt-unused-item-actions">
                                <button
                                  className="automgmt-restore-btn"
                                  onClick={() => toggleUnused(automation.automation_id, true)}
                                  title="Restore to main grid"
                                >
                                  ↩ Restore
                                </button>
                                <label className="automgmt-toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={automation.enabled}
                                    onChange={() => toggleAutomation(automation.automation_id, automation.enabled)}
                                  />
                                  <span className="automgmt-toggle-slider"></span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {editModalOpen && selectedAutomation && (
        <div className="automgmt-modal-overlay" onClick={closeEditModal}>
          <div className="automgmt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="automgmt-modal-header">
              <h2>Edit Schedule</h2>
              <button className="automgmt-modal-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="automgmt-modal-body">
              <div className="automgmt-form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                  className="automgmt-form-input"
                  placeholder="Automation name"
                />
              </div>

              <div className="automgmt-form-group">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="automgmt-form-textarea"
                  placeholder="What does this automation do?"
                  rows={3}
                />
              </div>
              
              <div className="automgmt-form-group">
                <label>Schedule Type</label>
                <select
                  value={editForm.schedule_type}
                  onChange={(e) => setEditForm({...editForm, schedule_type: e.target.value})}
                  className="automgmt-form-select"
                >
                  <option value="interval">Run on interval</option>
                  <option value="crontab">Run daily at specific time</option>
                </select>
              </div>

              {editForm.schedule_type === 'interval' ? (
                <div className="automgmt-form-group">
                  <label>How often?</label>
                  <select
                    value={editForm.interval_seconds}
                    onChange={(e) => setEditForm({...editForm, interval_seconds: parseInt(e.target.value, 10)})}
                    className="automgmt-form-select"
                  >
                    {getIntervalOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="automgmt-form-group">
                  <label>What time?</label>
                  <input
                    type="time"
                    value={`${editForm.crontab_hour.padStart(2, '0')}:${editForm.crontab_minute.padStart(2, '0')}`}
                    onChange={(e) => {
                      const [hour, minute] = e.target.value.split(':');
                      setEditForm({...editForm, crontab_hour: hour, crontab_minute: minute, crontab_day_of_week: '*'});
                    }}
                    className="automgmt-form-input automgmt-time-input"
                  />
                </div>
              )}

              <div className="automgmt-form-group">
                <label>Category</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  className="automgmt-form-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{formatCategoryName(cat)}</option>
                  ))}
                </select>
              </div>

              <div className="automgmt-linking-section">
                <h4>Link to Process Group</h4>
                <p className="automgmt-linking-help">Group related automations together visually</p>
                <div className="automgmt-form-group">
                  <label>Process Group</label>
                  <div className="automgmt-group-input-row">
                    <input
                      type="text"
                      value={editForm.process_group}
                      onChange={(e) => setEditForm({...editForm, process_group: e.target.value})}
                      className="automgmt-form-input"
                      placeholder="e.g., Content Pipeline, Payment Flow"
                      list="process-groups-list"
                    />
                    <datalist id="process-groups-list">
                      {getProcessGroups().map(group => (
                        <option key={group} value={group} />
                      ))}
                    </datalist>
                  </div>
                </div>
                {editForm.process_group && (
                  <div className="automgmt-form-group">
                    <label>Step Order</label>
                    <select
                      value={editForm.process_order}
                      onChange={(e) => setEditForm({...editForm, process_order: parseInt(e.target.value, 10)})}
                      className="automgmt-form-select"
                    >
                      <option value="1">Step 1</option>
                      <option value="2">Step 2</option>
                      <option value="3">Step 3</option>
                      <option value="4">Step 4</option>
                      <option value="5">Step 5</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="automgmt-modal-footer">
              <button className="automgmt-btn-cancel" onClick={closeEditModal}>Cancel</button>
              <button 
                className="automgmt-btn-save" 
                onClick={saveSchedule}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationManager;

